import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  FaArrowDown,
  FaBolt,
  FaBullhorn,
  FaChartLine,
  FaChevronDown,
  FaCode,
  FaEnvelope,
  FaGlobe,
  FaPlug,
  FaRobot,
  FaSearch,
  FaServer,
  FaShieldAlt,
  FaShoppingBasket,
  FaShoppingCart,
  FaSyncAlt,
  FaUniversity,
  FaYoutube,
} from 'react-icons/fa';
import { CtaButtons } from '../components/landing';
import { PillButtons, Input, Button } from '../components/input';
import { CodeBlock } from '../components/code';
import { RunCrawl, RunExtract, RunScrape } from '../components/run';
import { AgentPrompt } from '../components/agent';
import { Track, track } from '../components/track';
import { billingPlans } from '../components/billingPlans/plans';
import { narrow } from '../shared';
import { colors, headerHeight } from '../constants';

const headerStyle = {
  fontWeight: 'bold',
  fontSize: 26,
  textAlign: 'center',
};

const subHeaderStyle = {
  textAlign: 'center',
  fontSize: 22,
  fontWeight: 'bold',
  marginBottom: 20,
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const mailtoHref = (subject) => `mailto:help@fetchfoxai.com?subject=${encodeURIComponent(subject)}`;

const landingSectionStyle = {
  maxWidth: 1060,
  margin: '0 auto',
  padding: '54px 20px',
};

const eyebrowStyle = {
  color: colors.primaryDark,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0,
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: 8,
};

const sectionTitleStyle = {
  fontSize: 30,
  fontWeight: 800,
  textAlign: 'center',
  marginBottom: 10,
};

const sectionTextStyle = {
  maxWidth: 720,
  margin: '0 auto 26px auto',
  color: '#555',
  fontSize: 17,
  textAlign: 'center',
  lineHeight: 1.45,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 14,
};

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 198,
  border: '1px solid #e8e8e8',
  borderRadius: 8,
  padding: 18,
  background: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
};

const expandedCardStyle = {
  borderColor: colors.primary,
  boxShadow: '0 8px 28px rgba(251, 128, 20, 0.12)',
};

const iconWrapStyle = {
  width: 34,
  height: 34,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: colors.primaryLight,
  color: colors.primaryDark,
  marginBottom: 12,
};

const FalseDoorCard = ({ item, eventName, propertyName }) => {
  const [open, setOpen] = useState(false);
  const Icon = item.icon || FaBolt;
  const propertyValue = item.value || slugify(item.title);

  const handleOpen = () => {
    setOpen((wasOpen) => !wasOpen);
    track(eventName, { [propertyName]: propertyValue });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleLearnMore = (e) => {
    e.stopPropagation();
    track(eventName, { [propertyName]: propertyValue, action: 'learn_more' });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      style={{ ...cardStyle, ...(open ? expandedCardStyle : {}) }}
    >
      <div style={iconWrapStyle}>
        <Icon size={16} />
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{item.title}</div>
        <FaChevronDown
          size={14}
          color="#999"
          style={{ flexShrink: 0, marginTop: 4, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </div>
      <div style={{ color: '#666', fontSize: 14, lineHeight: 1.45, marginTop: 8 }}>{item.summary}</div>

      {open && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eee' }}>
          <div style={{ color: '#444', fontSize: 14, lineHeight: 1.45 }}>{item.details}</div>
          {!!item.points?.length && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              {item.points.map((point) => (
                <span
                  key={point}
                  style={{
                    border: '1px solid #e4e4e4',
                    borderRadius: 999,
                    padding: '4px 8px',
                    fontSize: 12,
                    color: '#555',
                    background: '#fafafa',
                  }}
                >
                  {point}
                </span>
              ))}
            </div>
          )}
          <a
            href={mailtoHref(item.subject || `Tell me more about ${item.title}`)}
            onClick={handleLearnMore}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              marginTop: 16,
              color: colors.primaryDark,
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            <FaEnvelope size={13} /> Learn More
          </a>
        </div>
      )}
    </div>
  );
};

const shuffled = (items) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const FalseDoorSection = ({ eyebrow, title, text, items, eventName, propertyName }) => {
  const randomizedItems = useMemo(() => shuffled(items), [items]);

  return (
    <section style={landingSectionStyle}>
      {eyebrow && <div style={eyebrowStyle}>{eyebrow}</div>}
      <div style={sectionTitleStyle}>{title}</div>
      {text && <div style={sectionTextStyle}>{text}</div>}
      <div style={gridStyle}>
        {randomizedItems.map((item) => (
          <FalseDoorCard key={item.title} item={item} eventName={eventName} propertyName={propertyName} />
        ))}
      </div>
    </section>
  );
};

const platformCards = [
  {
    title: 'Get the raw page HTML',
    value: 'raw_html',
    icon: FaCode,
    summary: 'Fetch the source you need for your own parser, archive, diff, or inspection workflow.',
    details:
      'Use FetchFox when you want the page body itself: original HTML, rendered HTML after JavaScript, or page content you can store and process later.',
    points: ['Source HTML', 'Rendered DOM', 'Content snapshots'],
    subject: 'I want raw HTML from FetchFox',
  },
  {
    title: 'Extract structured JSON',
    value: 'structured_json',
    icon: FaBolt,
    summary: 'Describe the fields you want and get back clean objects instead of a pile of markup.',
    details:
      'Turn product pages, search results, articles, directories, and other web pages into predictable JSON for apps, agents, and internal data jobs.',
    points: ['Schema-like prompts', 'Typed fields', 'Ready for apps'],
    subject: 'I want structured JSON from FetchFox',
  },
  {
    title: 'Run pages in a browser',
    value: 'browser_rendering',
    icon: FaGlobe,
    summary: 'Handle pages where the useful content only appears after client-side code runs.',
    details:
      'Visit dynamic sites with a real browser session, wait for the right state, then return the rendered content for extraction or downstream processing.',
    points: ['JavaScript pages', 'Wait conditions', 'Rendered content'],
    subject: 'I want browser rendering in FetchFox',
  },
  {
    title: 'Choose proxies automatically',
    value: 'proxy_rotation',
    icon: FaSyncAlt,
    summary: 'Let the scraping job use stronger access paths when a plain request is not enough.',
    details:
      'Start simple, then reach for premium proxy options, regional routing, or browser-backed access when the target site needs more care.',
    points: ['Routing choices', 'Regional access', 'Tougher targets'],
    subject: 'I want automated proxy rotation in FetchFox',
  },
];

const useCaseCards = [
  {
    title: 'E-commerce',
    value: 'ecommerce',
    icon: FaShoppingCart,
    summary: 'Keep tabs on assortment, price movement, inventory signals, and reviews.',
    details:
      'Use recurring jobs to collect product pages, category pages, seller data, and marketplace results without writing a new scraper for every storefront.',
    points: ['Assortment', 'Inventory', 'Reviews'],
  },
  {
    title: 'AI',
    icon: FaRobot,
    summary: 'Give agents and LLM systems current web context in a shape they can consume.',
    details:
      'Pull documentation, search results, product data, competitive pages, or niche research material into RAG, evaluation, and agent workflows.',
    points: ['RAG context', 'Agent tools', 'Evaluation data'],
  },
  {
    title: 'Market intelligence',
    icon: FaChartLine,
    summary: 'Track public signals across competitors, categories, search pages, and content.',
    details:
      'Build watchlists for category movement, site changes, SEO visibility, messaging shifts, and other public web signals your team cares about.',
    points: ['Competitors', 'Search visibility', 'Trend tracking'],
  },
  {
    title: 'Fintech',
    icon: FaUniversity,
    summary: 'Gather public-source signals for research, monitoring, and risk workflows.',
    details:
      'Collect filings, pricing pages, public listings, news-adjacent sources, and alternative data streams into repeatable pipelines.',
    points: ['Public sources', 'Risk signals', 'Research feeds'],
  },
  {
    title: 'GTM',
    value: 'gtm',
    icon: FaBullhorn,
    summary: 'Bring account research and buying signals into sales and marketing motions.',
    details:
      'Scrape company pages, job posts, locations, technology mentions, pricing pages, and other web context for enrichment and prioritization.',
    points: ['Account research', 'Lead enrichment', 'Signals'],
  },
  {
    title: 'Cybersecurity',
    icon: FaShieldAlt,
    summary: 'Collect public web evidence for investigations, monitoring, and discovery.',
    details:
      'Monitor search results, exposed pages, public forums, impersonation signals, or asset lists, then hand structured findings to your security tooling.',
    points: ['Threat research', 'Exposure checks', 'Brand monitoring'],
  },
];

const apiCards = [
  {
    title: 'HTTP API',
    value: 'http_api',
    icon: FaCode,
    summary: 'Submit scraping, crawling, visiting, and extraction jobs from any stack.',
    details:
      'Use plain HTTP from a backend, worker, notebook, cron job, or command line when you want a direct programmable interface.',
    points: ['POST requests', 'curl friendly', 'JSON responses'],
  },
  {
    title: 'JavaScript',
    value: 'javascript',
    icon: FaCode,
    summary: 'Run FetchFox jobs from Node, TypeScript, serverless functions, or scripts.',
    details:
      'Use JavaScript when your scraping workflow belongs inside an app backend, queue worker, CLI tool, or data pipeline already written in Node.',
    points: ['Node', 'TypeScript', 'Serverless'],
    subject: 'I want the FetchFox JavaScript API',
  },
  {
    title: 'Python',
    value: 'python',
    icon: FaCode,
    summary: 'Call FetchFox from notebooks, data jobs, research scripts, and backend services.',
    details:
      'Use Python for analysis-heavy scraping flows, scheduled ETL jobs, AI experiments, or teams that prefer to process web data with Python tooling.',
    points: ['Notebooks', 'ETL', 'Data science'],
    subject: 'I want the FetchFox Python API',
  },
  {
    title: 'MCP',
    value: 'mcp',
    icon: FaServer,
    summary: 'Expose FetchFox as a tool that compatible AI agents can call during a task.',
    details:
      'Connect through MCP so an agent can request page visits, crawls, and extractions with your FetchFox account rather than a fragile browser script.',
    points: ['Tool calling', 'Agent context', 'Remote server'],
  },
  {
    title: 'OpenClaw',
    value: 'openclaw',
    icon: FaRobot,
    summary: 'Connect FetchFox scraping tools to OpenClaw-style agent workflows.',
    details:
      'Use FetchFox from agent runtimes that need page visits, extraction, crawl jobs, and structured web context during a task.',
    points: ['Agent workflows', 'Tool calling', 'Web context'],
  },
  {
    title: 'n8n',
    value: 'n8n',
    icon: FaPlug,
    summary: 'Run web-data steps inside the workflows your ops team already maintains.',
    details:
      'Trigger a FetchFox job, pass the result to later n8n nodes, and keep enrichment or monitoring logic in the same automation canvas.',
    points: ['Workflow nodes', 'Enrichment', 'Recurring jobs'],
  },
  {
    title: 'Zapier',
    value: 'zapier',
    icon: FaPlug,
    summary: 'Move scrape results into spreadsheets, CRMs, alerts, and team tools.',
    details: 'Use lightweight triggers and actions to send web data into the business apps your team checks every day.',
    points: ['Triggers', 'Business apps', 'Notifications'],
  },
  {
    title: 'Make',
    value: 'make',
    icon: FaPlug,
    summary: 'Add scraping jobs to Make scenarios without building a custom connector.',
    details:
      'This would make it easier to feed web data into sheets, databases, approvals, notifications, and internal tools managed in Make.',
    points: ['Scenarios', 'Databases', 'Approvals'],
  },
  {
    title: 'Content formats',
    value: 'markdown_extraction',
    icon: FaBolt,
    summary: 'Choose HTML, text, Markdown, links, images, or extracted fields from one job.',
    details:
      'Different pipelines need different payloads. Use the format that fits your parser, LLM prompt, storage layer, or human review step.',
    points: ['HTML', 'Markdown', 'Structured fields'],
  },
  {
    title: 'JavaScript SDK',
    value: 'javascript_sdk',
    icon: FaCode,
    summary: 'Call FetchFox from Node or TypeScript without hand-rolling request helpers.',
    details:
      'Configure your key once, start jobs from application code, and handle the structured response in the same project as the rest of your data logic.',
    points: ['Node', 'TypeScript', 'App code'],
  },
];

const scraperCards = [
  {
    title: 'Google Search API',
    value: 'google_search_api',
    icon: FaSearch,
    summary: 'Normalized search results for tracking visibility, rankings, and web mentions.',
    details:
      'Turn result pages into clean data for SEO monitoring, market research, rank tracking, and AI-context systems.',
    points: ['Organic results', 'Rank checks', 'Search modules'],
  },
  {
    title: 'YouTube API',
    value: 'youtube_api',
    icon: FaYoutube,
    summary: 'Collect channel, video, search, transcript, or comment data in a stable shape.',
    details:
      'This would sit above the generic browser and extraction tools for teams that repeatedly need YouTube data without maintaining page-specific logic.',
    points: ['Channels', 'Transcripts', 'Comments'],
  },
  {
    title: 'Amazon API',
    value: 'amazon_api',
    icon: FaShoppingCart,
    summary: 'Pull product pages, marketplace rankings, offers, and review signals.',
    details:
      'Vote here if a maintained Amazon-specific endpoint would save you from parser churn around product detail pages and search/category results.',
    points: ['Offers', 'Rankings', 'Reviews'],
  },
  {
    title: 'Walmart API',
    value: 'walmart_api',
    icon: FaShoppingBasket,
    summary: 'Track product detail pages, shelves, prices, availability, and ratings.',
    details:
      'A Walmart-focused endpoint could return retailer fields directly, which is useful for assortment checks, repricing, and catalog monitoring.',
    points: ['Retail shelves', 'Availability', 'Ratings'],
  },
  {
    title: 'ChatGPT API',
    value: 'chatgpt_api',
    icon: FaRobot,
    summary: 'Monitor AI answers, citations, and generated recommendations over time.',
    details:
      'Query answer engines, capture responses, and structure them for evaluation, brand tracking, competitive research, or monitoring.',
    points: ['Answer tracking', 'Citations', 'Evals'],
  },
  {
    title: 'Home Depot API',
    value: 'home_depot_api',
    icon: FaShoppingBasket,
    summary: 'Home-improvement catalog data for price, stock, location, and review tracking.',
    details:
      'Click here if you would use a maintained scraper for store-aware retail pages, local availability, product specs, or catalog comparison.',
    points: ['Store-aware data', 'Specs', 'Local stock'],
  },
];

const faqCards = [
  {
    title: 'When should I use FetchFox?',
    value: 'when_should_i_use_fetchfox',
    summary: 'Use it when a workflow needs web data but you do not want to babysit scrapers.',
    details:
      'It is especially useful for recurring crawls, dynamic pages, extraction into JSON, agent tooling, and data jobs where a plain fetch is not enough.',
    subject: 'Question: when to use FetchFox',
  },
  {
    title: 'How quickly do requests finish?',
    value: 'speed_and_hard_sites',
    summary: 'Simple requests can be quick; browser, proxy, and AI-heavy work takes longer.',
    details:
      'The timing depends on the page, whether JavaScript must run, how much content needs extraction, and whether the request needs stronger access options.',
    subject: 'Question: FetchFox speed and hard sites',
  },
  {
    title: 'Do I need to write code?',
    value: 'non_developers',
    summary: 'The product is developer-first, with no-code and agent paths for lighter workflows.',
    details:
      'The prompt interface helps with exploration. n8n, Make, Zapier, and agent integrations let teams move web data through workflows without writing a full app.',
    subject: 'Question: FetchFox for non-developers',
  },
  {
    title: 'Can it handle JavaScript-heavy sites?',
    value: 'javascript_interaction',
    summary: 'Yes, use browser-backed visits when the first HTML response is not enough.',
    details:
      'FetchFox can visit the page in a browser-like environment, wait for content, and extract from the rendered DOM when a static request misses the useful data.',
    subject: 'Question: JavaScript rendering in FetchFox',
  },
  {
    title: 'Why have both general and specialized APIs?',
    value: 'http_vs_specialized',
    summary: 'The general API is flexible; specialized APIs trade flexibility for convenience.',
    details:
      'Use the general API for custom jobs across many sites. Vote for specialized APIs when you want a maintained endpoint for one source with fields already normalized.',
    subject: 'Question: HTTP API vs specialized APIs',
  },
  {
    title: 'What am I charged for?',
    value: 'usage_pricing',
    summary: 'Usage is based on API work, with heavier infrastructure reflected separately.',
    details:
      'Basic jobs and more expensive workflows have different cost profiles. Browser usage, premium proxies, and AI extraction can change the cost of a run.',
    subject: 'Question: FetchFox usage pricing',
  },
  {
    title: 'What happens when volume grows?',
    value: 'outgrow_monthly_usage',
    summary: 'Higher plans and custom setups can raise limits, concurrency, and support.',
    details:
      'Large workloads usually need the right mix of concurrency, target-site strategy, proxy choices, and retry behavior. We can tune that around the actual job.',
    subject: 'Question: FetchFox monthly usage',
  },
  {
    title: 'How do I test a target site?',
    value: 'trial_before_paying',
    summary: 'Start with the prompt box, inspect the result, then turn the working setup into an API call.',
    details:
      'Trying a URL first helps you learn whether the site needs raw HTML, browser rendering, extraction instructions, proxies, or a crawl pattern.',
    subject: 'Question: trying FetchFox',
  },
  {
    title: 'How do failures show up?',
    value: 'failed_requests',
    summary: 'Failures are surfaced through job state and response details so you can adjust.',
    details:
      'A failure might mean the page needs rendering, the extraction prompt needs more detail, the site is blocking access, or the crawl shape needs to be narrowed.',
    subject: 'Question: failed FetchFox requests',
  },
  {
    title: 'Is there a long commitment?',
    value: 'cancel_any_time',
    summary: 'No long contract is required for the standard self-serve path.',
    details:
      'For larger custom plans, we can discuss terms that fit the workload, support needs, and expected monthly volume.',
    subject: 'Question: canceling FetchFox',
  },
  {
    title: 'Can I run many jobs at once?',
    value: 'more_concurrency',
    summary: 'Yes, concurrency can scale for teams with larger or time-sensitive workloads.',
    details:
      'The right limit depends on your target sites, expected volume, latency goals, and how much browser or proxy capacity the workload needs.',
    subject: 'Question: FetchFox concurrency',
  },
];

const McpChoices = () => {
  const [tab, setTab] = useState('python');

  const choices = [['python', 'Python (pydantic)']];

  const code = {
    python: `# pip install pydantic-ai

from pydantic_ai import Agent
from pydantic_ai.mcp import MCPServerStreamableHTTP
from pydantic import BaseModel

fetchfox = MCPServerStreamableHTTP(
    url='https://mcp.fetchfox.ai/mcp',
    headers={"Authorization": "Bearer YOUR_API_KEY"})

agent = Agent('openai:gpt-4o', toolsets=[fetchfox])
result = agent.run_sync(
  'Scrape https://pokemondb.net/pokedex/* for 10 pokemon names and numbers'
)`,
    javascript: `// npm install modelcontextprotocol

import { Client } from "modelcontextprotocol/client";
import { HTTPServerTransport } from "modelcontextprotocol/transport/http";

async function run() {
  const client = new Client(
    { name: "demo", version: "1.0.0" },
    new HTTPServerTransport({ url: "http://localhost:3000/mcp" })
  );

  await client.connect();
  console.log(await client.listTools());

  const result = await client.callTool("echo", { message: "hi" });
  console.log(result);

  await client.close();
}

run();
`,
  };

  return (
    <>
      <PillButtons choices={choices} value={tab} onChange={setTab} />
      <CodeBlock language={tab}>{code[tab]}</CodeBlock>
      <FaArrowDown size="36" color="#888" />
    </>
  );
};

const Pricing = () => {
  const gridStyle = {
    width: '100%',
    maxWidth: 1060,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
    alignItems: 'stretch',
  };

  const planCardStyle = {
    border: '1px solid #ddd',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 480,
    background: 'white',
    textAlign: 'left',
  };

  const cardHeaderStyle = {
    padding: 18,
    borderBottom: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  const badgeStyle = {
    alignSelf: 'flex-start',
    border: '1px solid #fed7aa',
    borderRadius: 999,
    padding: '3px 8px',
    color: '#c2410c',
    background: '#fff7ed',
    fontSize: 12,
    fontWeight: 'bold',
  };

  const priceStyle = {
    fontSize: 30,
    fontWeight: 'bold',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 14,
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
    lineHeight: 1.35,
  };

  const actionForPlan = (plan) => {
    if (plan.href) {
      return {
        href: plan.href,
        label: plan.buttonLabel,
      };
    }

    return {
      to: '/billing',
      label: plan.id == 'monthly_10' ? 'Get 50% discount' : plan.buttonLabel,
    };
  };

  return (
    <section
      id="pricing"
      style={{
        ...landingSectionStyle,
        scrollMarginTop: headerHeight + 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div style={headerStyle}>Pricing</div>
      <div style={sectionTextStyle}>
        Choose flexible credits or the monthly subscription with 50% lower usage rates.
      </div>

      <div style={gridStyle}>
        {billingPlans.map((plan) => {
          const action = actionForPlan(plan);

          return (
            <div key={plan.id} style={planCardStyle}>
              <div style={cardHeaderStyle}>
                <div style={badgeStyle}>{plan.badge}</div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 'bold' }}>{plan.title}</div>
                  <div style={{ color: '#666', marginTop: 6, minHeight: 38, lineHeight: 1.35 }}>{plan.subtitle}</div>
                </div>
                <div style={priceStyle}>{plan.price}</div>
              </div>

              <div style={{ padding: '8px 18px 18px', flex: 1 }}>
                {plan.rows.map(([label, value]) => (
                  <div key={label} style={rowStyle}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <strong style={{ textAlign: 'right' }}>{value}</strong>
                  </div>
                ))}
              </div>

              <div style={{ padding: 18, borderTop: '1px solid #eee' }}>
                <Button
                  style={{ width: '100%' }}
                  to={action.to}
                  href={action.href}
                  onClick={() => track('landing/pricing_plan_click', { plan: plan.id })}
                >
                  {action.label}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ApiCall = () => {
  const [tab, setTab] = useState('javascript');

  const choices = [
    // ['python', 'Python'],
    ['javascript', 'JavaScript'],
    // ['mcp', 'MCP Server'],
    // ['pydantic', 'Pydantic'],
    ['bash', 'curl'],
  ];

  const code = {
    python: `# pip install fetchfox-sdk

from fetchfox_sdk import FetchFox

fox = FetchFox(api_key="YOUR_API_KEY")

data = fox.scrape({
  "pattern": 'https://www.reddit.com/r/*/comments/*',
  "template": "username, comment, points",
})`,

    javascript: `// npm install fetchfox-sdk

import * as fox from 'fetchfox-sdk';

fox.configure({ apiKey: 'YOUR_API_KEY' })

const data = await fox.scrape({
  pattern: 'https://www.reddit.com/r/*/comments/*',
  template: 'username, comment, points'
})`,

    mcp: `{
  "url": "https://mcp.fetchfox.ai/mcp",
  "transport": {
    "type": "http",
    "headers": {
      "Authorization": "Bearer YOUR_API_KEY"
    }
}

# And then ask your agent to scrape something`,

    pydantic: `from pydantic_ai import Agent
from pydantic_ai.mcp import MCPServerStreamableHTTP

fetchfox = MCPServerStreamableHTTP(
    url='https://mcp.fetchfox.ai/mcp',
    headers={"Authorization": "Bearer YOUR_API_KEY"})

agent = Agent('openai:gpt-4o', toolsets=[fetchfox])
result = agent.run_sync(
  'Scrape username, text, and points from '
  'https://www.reddit.com/r/*/comments/*')`,

    bash: `curl -X POST https://api.fetchfox.ai/api/scrape \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $FETCHFOX_API_KEY" \\
  -d '{"pattern":"https://www.reddit.com/r/*/comments/*",
       "template": "username, comment, points"}'`,
  };

  const language =
    {
      mcp: 'bash',
      pydantic: 'python',
    }[tab] || tab;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <br />

      <div style={subHeaderStyle}>Scrape millions of pages with one line of code</div>
      <PillButtons choices={choices} value={tab} onChange={setTab} />

      <CodeBlock language={language}>{code[tab]}</CodeBlock>
      <FaArrowDown size="36" color="#888" />

      {/*tab == 'mcp' && <McpChoices />*/}

      <CodeBlock language="json">
        {`{
    "username": "BobbyUser",
    "comment_text": "AI + FetchFox = 🤩",
    "points": 888
}`}
      </CodeBlock>

      <div style={{ color: '#888', fontSize: 14, margin: '40px 0 0 0' }}>🪦 BeautifulSoup 2004-2025, RIP</div>
    </div>
  );
};

//   Scrape <mark>any site</mark>
// </div>
// <div style={headerStyle}>
//   with <mark>just a prompt</mark>

const StartPrompt = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate('/sandbox', { state: { url } });
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 5, position: 'relative', width: '100%' }}>
        <Input
          style={{ width: '100%' }}
          type="text"
          placeholder="https://..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <div style={{}}>
          <Button>Start</Button>
        </div>
      </form>
    </div>
  );
};

export function LandingCurrent() {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          textAlign: 'center',
          marginx: '40px auto 80px auto',
          height: '60vh',
        }}
      >
        <div style={{ flex: 1 }}></div>
        <div>
          <img src="/logo-fox.svg" style={{ width: 50, marginBottom: 10 }} />
        </div>
        {/*
        <div style={headerStyle}>
          Scrape <mark>any site</mark> with just <mark>a prompt</mark>
        </div>
        */}
        <div style={headerStyle}>Scrape any site with just a prompt</div>
        <div style={{ color: '#444', textAlign: 'center', fontSize: 22, marginBottom: 10 }}>
          An AI scraper for developers
        </div>

        <div style={{ width: '100%', maxWidth: 640, margin: '40px 0' }}>
          <AgentPrompt />
        </div>

        {/* <StartPrompt /> */}
        <div style={{ flex: 2 }}></div>

        {/*
        <div style={{ fontSize: 15, textAlign: 'center', color: '#777' }}>
          Scrape <b style={{ color: '#555' }}>millions of pages</b> and convert them into <br /> structured data with
          just <b style={{ color: '#555' }}>one line of code</b>.
        </div>
        */}
      </div>

      <FalseDoorSection
        eyebrow="Scraping infrastructure"
        title="Built for teams that scrape at scale"
        text="Start with a prompt, then use the right output, browser, and access options as your scraping workflow gets more specific."
        items={platformCards}
        eventName="landing/capability"
        propertyName="capability"
      />

      <FalseDoorSection
        eyebrow="Use cases"
        title="Built for every use case"
        text="FetchFox can start broad with a prompt and become precise as your workflow hardens into repeatable data products."
        items={useCaseCards}
        eventName="landing/use-case"
        propertyName="use_case"
      />

      <FalseDoorSection
        eyebrow="API and integrations"
        title="Use the API"
        text="Choose the interface that matches how you work: direct HTTP calls, agent tools, no-code automations, or source-specific features."
        items={apiCards}
        eventName="landing/api"
        propertyName="api"
      />

      <FalseDoorSection
        eyebrow="Specialized scrapers"
        title="Dedicated data APIs"
        text="Use higher-level scrapers when you want normalized data from a known source without maintaining a custom parser."
        items={scraperCards}
        eventName="landing/scraper"
        propertyName="scraper"
      />

      <FalseDoorSection
        eyebrow="FAQ"
        title="Common questions"
        text="Open a question to see the short version. The Learn More link opens an email with the right subject line already filled in."
        items={faqCards}
        eventName="landing/faq"
        propertyName="faq"
      />

      {narrow(<ApiCall />)}

      <br />
      <br />

      <Pricing />

      <br />
      <br />

      <CtaButtons />
      <br />
      <br />
      <br />

      {/*
      <div style={{ margin: '40px auto 200px auto', width: 640, textAlign: 'center', fontSize: 18 }}>
        FetchFox is a new
        <div style={{ margin: '18px 0 10px 0' }}>
          <FaArrowDown size="24" color="#aaa" />
        </div>
        Get free 1:1 support on{' '}
        <a href={discordUrl} className="bold">
          Discord
        </a>{' '}
        or at{' '}
        <a href="mailto:help@fetchfoxai.com" className="bold">
          help@fetchfoxai.com
        </a>
      </div>
      */}

      <Track name="page/landing" />
    </div>
  );
}

export default LandingCurrent;
